import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import ArqueoCaja from '../components/caja/ArqueoCaja';
// import ArqueoTable from '../components/caja/ArqueoTable';
import ArqueoDistribucion from '../components/caja/ArqueoDistribucion';
import CajaMovimientosModal from '../components/caja/CajaMovimientosModal'; // Import new modal
import ModalEntregarEfectivo from '../components/caja/ModalEntregarEfectivo'; // Import new modal
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { cajaService } from '../services/cajaService';
import { habilitacionService } from '../services/habilitacionService'; // Import missing service
import { generateReporteCajaHTML } from '../utils/reporteCajaGenerator';
import './GestionCaja.css';

const GestionCaja = () => {
    const { user } = useAuth();
    const { theme } = useTheme();
    const [sesionActual, setSesionActual] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage] = useState(15);
    const [searchDate, setSearchDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [showArqueo, setShowArqueo] = useState(false);
    const [montoApertura, setMontoApertura] = useState('');
    const [activeTab, setActiveTab] = useState('personal'); // 'personal' | 'global'
    const [globalData, setGlobalData] = useState(null);
    const [globalDate, setGlobalDate] = useState(new Date().toLocaleDateString('en-CA')); // Default today (Local)
    const [saldoSugerido, setSaldoSugerido] = useState(0);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [modalFilter, setModalFilter] = useState('ALL'); // 'ALL' | 'INCOME' | 'EXPENSE'
    const [selectedSesionId, setSelectedSesionId] = useState(null); // For session details modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [sesionToEdit, setSesionToEdit] = useState(null);
    const [ingresosCalculados, setIngresosCalculados] = useState(0);
    const [editForm, setEditForm] = useState({
        monto_inicial: '',
        monto_final_real: '',
        ingresos_sistema: '',
        observaciones: ''
    });

    // Estado para reporte detallado (tooltip)
    const [analysisData, setAnalysisData] = useState(null);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);

    // Estados para Habilitaciones (Insertado)
    const [showEntregarModal, setShowEntregarModal] = useState(false);
    const [pendientes, setPendientes] = useState([]);
    const [loadingPendientes, setLoadingPendientes] = useState(false);

    // SweetAlert2 Helper with Dark Mode Support
    const showAlert = (options) => {
        const isDark = theme === 'dark';
        const brandColors = {
            success: '#10b981',
            error: '#ef4444',
            warning: '#f59e0b',
            info: '#3b82f6',
            question: '#667eea',
        };

        const getIconColor = () => {
            if (options.icon === 'success') return brandColors.success;
            if (options.icon === 'error') return brandColors.error;
            if (options.icon === 'warning') return brandColors.warning;
            if (options.icon === 'info') return brandColors.info;
            if (options.icon === 'question') return brandColors.question;
            return brandColors.info;
        };

        return Swal.fire({
            background: isDark ? '#000000' : '#ffffff',
            color: isDark ? '#e2e2e2' : '#3f4254',
            confirmButtonColor: getIconColor(),
            cancelButtonColor: '#7e8299',
            iconColor: getIconColor(),
            ...options,
            customClass: {
                popup: 'caja-alert',
                confirmButton: 'btn-pill',
                cancelButton: 'btn-pill',
                ...options.customClass
            }
        });
    };

    useEffect(() => {
        if (user) {
            fetchEstadoCaja();
            fetchGlobalSummary();
            fetchHistorial(activeTab === 'global', 1);
        }
    }, [user, activeTab, globalDate]); // Re-fetch when date changes

    const fetchGlobalSummary = async () => {
        try {
            const data = await cajaService.obtenerResumenGlobal(globalDate);
            setGlobalData(data);

            // Fetch analysis for global view
            fetchAnalysisReport(null, globalDate, globalDate);
        } catch (error) {
            console.error('Error fetching global summary:', error);
        }
    };

    const fetchEstadoCaja = async () => {
        try {
            const data = await cajaService.consultarEstado(user.id);
            if (data.abierta) {
                setSesionActual(data);
                // Fetch analysis for current session (Pass dates to filter out all-time historical data)
                const sessionDate = new Date(data.fecha_apertura).toLocaleDateString('en-CA');
                const todayDate = new Date().toLocaleDateString('en-CA');
                fetchAnalysisReport(user.id, sessionDate, todayDate);
            } else {
                setSesionActual(null);
                const sugerido = data.saldo_sugerido || 0;
                setSaldoSugerido(sugerido);
                setMontoApertura(sugerido > 0 ? sugerido.toString() : '');
                setAnalysisData(null);
            }
        } catch (error) {
            console.error('Error fetching caja status:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAnalysisReport = async (userId = null, fechaDesde = null, fechaHasta = null, sesionId = null) => {
        setLoadingAnalysis(true);
        try {
            const params = {};
            if (userId) params.usuario_id = userId;
            if (fechaDesde) params.fechaDesde = fechaDesde;
            if (fechaHasta) params.fechaHasta = fechaHasta;
            if (sesionId) params.sesion_id = sesionId;

            const report = await cajaService.obtenerReporteGlobalConceptos(params);
            setAnalysisData(report);
        } catch (error) {
            console.error('Error fetching analysis report:', error);
        } finally {
            setLoadingAnalysis(false);
        }
    };

    const fetchHistorial = async (isGlobal = (activeTab === 'global'), page = 1) => {
        try {
            const filters = {
                page,
                limit: itemsPerPage,
                ...(isGlobal ? {} : { usuario_id: user.id })
            };
            if (searchDate) {
                filters.startDate = `${searchDate}T00:00:00`;
                filters.endDate = `${searchDate}T23:59:59`;
            }
            const res = await cajaService.listarHistorial(filters);
            if (res && res.sesiones) {
                setHistorial(res.sesiones);
                setTotalPages(res.totalPages || 1);
                setTotalItems(res.totalItems || 0);
                setCurrentPage(res.currentPage || 1);
            } else {
                setHistorial(res || []);
                setTotalPages(1);
                setTotalItems(res?.length || 0);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    useEffect(() => {
        if (user) {
            fetchHistorial(activeTab === 'global', 1);
        }
    }, [searchDate]);

    // Funciones de Habilitaciones (Insertado)
    const fetchPendientes = async () => {
        if (!user) return;
        setLoadingPendientes(true);
        try {
            const data = await habilitacionService.listarPendientes(user.id);
            setPendientes(data);
        } catch (error) {
            console.error('Error fetching pendientes:', error);
        } finally {
            setLoadingPendientes(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'personal') {
            fetchPendientes();
        }
    }, [user, activeTab]);

    const handleAceptarHabilitacion = async (habilitacion) => {
        try {
            const result = await showAlert({
                icon: 'question',
                title: '¿Recibir Dinero?',
                html: `
                    <p>Estás a punto de recibir <strong>S/ ${parseFloat(habilitacion.monto).toFixed(2)}</strong> de <strong>${habilitacion.emisor?.username}</strong>.</p>
                    <p class="text-muted small">${habilitacion.observaciones || ''}</p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Sí, Aceptar Dinero',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                await habilitacionService.aceptarHabilitacion(habilitacion.id, user.id);
                await showAlert({
                    icon: 'success',
                    title: '¡Dinero Recibido!',
                    text: 'El monto ha sido ingresado a tu caja correctamente.',
                    timer: 2000,
                    showConfirmButton: false
                });
                fetchPendientes();
                fetchEstadoCaja(); // Recargar caja para ver ingreso
            }
        } catch (error) {
            showAlert({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'No se pudo aceptar el dinero.'
            });
        }
    };

    const handleEditClick = async (sesion) => {
        setSesionToEdit(sesion);

        // Fetch movements to calculate real income
        try {
            const movimientos = await cajaService.obtenerMovimientosSesion(sesion.id);

            // Filter only EFECTIVO income (matching backend logic)
            const ingresosReales = movimientos
                .filter(mov => !mov.es_salida && (
                    mov.metodo?.toUpperCase() === 'EFECTIVO' ||
                    mov.descripcion?.toUpperCase().includes('EFECTIVO')
                ))
                .reduce((sum, mov) => sum + parseFloat(mov.monto || 0), 0);

            setIngresosCalculados(ingresosReales);

            setEditForm({
                monto_inicial: sesion.monto_inicial.toString(),
                monto_final_real: (sesion.monto_final_real || 0).toString(),
                ingresos_sistema: (sesion.ingresos_sistema || 0).toString(),
                observaciones: sesion.observaciones || ''
            });
        } catch (error) {
            console.error('Error fetching movements:', error);
            setIngresosCalculados(sesion.ingresos_sistema || 0);
            setEditForm({
                monto_inicial: sesion.monto_inicial.toString(),
                monto_final_real: (sesion.monto_final_real || 0).toString(),
                ingresos_sistema: (sesion.ingresos_sistema || 0).toString(),
                observaciones: sesion.observaciones || ''
            });
        }

        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        try {
            await cajaService.editarSesion(sesionToEdit.id, editForm);
            showAlert({
                icon: 'success',
                title: 'Sesión Actualizada',
                text: 'Los cambios se han guardado correctamente.'
            });
            setShowEditModal(false);
            fetchHistorial(activeTab === 'global');
            if (activeTab === 'personal') fetchEstadoCaja();
        } catch (error) {
            showAlert({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'No se pudo actualizar la sesión.'
            });
        }
    };

    const handlePrintReport = async (sesionId) => {
        try {
            const data = await cajaService.obtenerDatosReporte(sesionId);
            const htmlContent = generateReporteCajaHTML(data);

            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(htmlContent);
                printWindow.document.close();
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                }, 500);
            } else {
                showAlert({
                    title: 'Bloqueador de ventanas',
                    text: 'Por favor permita las ventanas emergentes para ver el reporte.',
                    icon: 'warning'
                });
            }
        } catch (error) {
            console.error('Error printing report:', error);
            showAlert({
                title: 'Error',
                text: 'No se pudieron cargar los datos del reporte.',
                icon: 'error'
            });
        }
    };

    const handleAbrirCaja = async () => {
        if (!montoApertura || parseFloat(montoApertura) < 0) {
            return showAlert({
                icon: 'warning',
                title: 'Monto Requerido',
                text: 'Por favor ingrese un monto inicial válido para abrir la caja',
                confirmButtonText: 'Entendido'
            });
        }
        try {
            await cajaService.abrirCaja({
                usuario_id: user.id,
                sede_id: user.sede_id || 1,
                monto_inicial: parseFloat(montoApertura)
            });
            await showAlert({
                icon: 'success',
                title: '¡Caja Abierta!',
                text: 'La sesión de caja se ha iniciado correctamente',
                timer: 2000,
                showConfirmButton: false
            });
            fetchEstadoCaja();
            fetchHistorial();
            setMontoApertura('');
        } catch (error) {
            showAlert({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.error || 'Error al abrir caja'
            });
        }
    };

    const handleCerrarCajaConfirm = async (arqueoData) => {
        console.log('🟢 GestionCaja: handleCerrarCajaConfirm called');
        console.log('🟢 GestionCaja: Arqueo data received:', arqueoData);
        console.log('🟢 GestionCaja: Sesion ID:', sesionActual?.sesion_id);
        try {
            const dataToSend = {
                ...arqueoData,
                observaciones: 'Cierre regular'
            };
            console.log('🟢 GestionCaja: Calling cerrarCaja with:', dataToSend);
            const result = await cajaService.cerrarCaja(sesionActual.sesion_id, dataToSend);
            console.log('🟢 GestionCaja: cerrarCaja successful, result:', result);
            setShowArqueo(false);
            fetchEstadoCaja();
            fetchHistorial();
            await showAlert({
                icon: 'success',
                title: '¡Caja Cerrada!',
                text: 'La sesión de caja se ha cerrado correctamente',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('🔴 GestionCaja: Error al cerrar caja:', error);
            console.error('🔴 GestionCaja: Error details:', error.response?.data || error.message);
            showAlert({
                icon: 'error',
                title: 'Error al Cerrar Caja',
                text: error.response?.data?.error || error.message
            });
        }
    };

    const handleReabrirSesion = async (sesionId) => {
        const result = await showAlert({
            icon: 'warning',
            title: '¿Reabrir Sesión de Caja?',
            html: `
                <p>Esta acción reabrirá la sesión de caja #${sesionId}.</p>
                <p><strong>Advertencia:</strong> Esto permitirá agregar nuevos movimientos a una sesión ya cerrada.</p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Sí, Reabrir',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await cajaService.reabrirSesion(sesionId);
                await showAlert({
                    icon: 'success',
                    title: '¡Sesión Reabierta!',
                    text: 'La sesión de caja ha sido reabierta correctamente',
                    timer: 2000,
                    showConfirmButton: false
                });
                fetchHistorial(activeTab === 'global');
                fetchEstadoCaja();
            } catch (error) {
                showAlert({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.error || 'Error al reabrir sesión'
                });
            }
        }
    };

    const handleEliminarSesion = async (sesionId) => {
        const result = await showAlert({
            icon: 'error',
            title: '¿Eliminar Sesión de Caja?',
            html: `
                <p>Esta acción eliminará <strong>permanentemente</strong> la sesión #${sesionId}.</p>
                <p><strong>⚠️ ADVERTENCIA:</strong> Esta acción no se puede deshacer.</p>
                <p>Todos los movimientos asociados también serán eliminados.</p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Sí, Eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#ef4444'
        });

        if (result.isConfirmed) {
            try {
                await cajaService.eliminarSesion(sesionId);
                await showAlert({
                    icon: 'success',
                    title: '¡Sesión Eliminada!',
                    text: 'La sesión de caja ha sido eliminada correctamente',
                    timer: 2000,
                    showConfirmButton: false
                });
                fetchHistorial(activeTab === 'global');
                fetchEstadoCaja();
            } catch (error) {
                showAlert({
                    icon: 'error',
                    title: 'Error',
                    text: error.response?.data?.error || 'Error al eliminar sesión'
                });
            }
        }
    };


    if (loading) return <div className="loading-spinner">Cargando...</div>;

    const renderDashboard = (isGlobal) => {
        const data = isGlobal ? globalData : sesionActual;
        const breakdown = isGlobal ? globalData?.desglose_global : sesionActual?.desglose_ingresos;

        if (isGlobal && !data) return <div className="loading-spinner">Cargando resumen global...</div>;
        if (!isGlobal && !sesionActual) {
            // Render Open Form
            return (
                <div className="status-card status-closed">
                    <div className="status-header">
                        <div className="status-icon"><i className="flaticon-381-close"></i></div>
                        <div className="status-info">
                            <h2>CAJA CERRADA</h2>
                            <p className="session-time">Debes abrir caja para comenzar a operar.</p>
                        </div>
                    </div>
                    <div className="open-session-form">
                        <div className="form-group mb-4">
                            <label className="fw-bold">Monto Inicial de Apertura (S/)</label>
                            <div className="input-group">
                                <span className="input-group-text">S/</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="form-control form-control-lg"
                                    placeholder="0.00"
                                    value={montoApertura}
                                    onChange={e => setMontoApertura(e.target.value)}
                                />
                            </div>
                            {saldoSugerido > 0 && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        Saldo sugerido (cierre anterior):
                                        <button
                                            className="btn btn-link btn-sm p-1 ms-1 text-primary fw-bold"
                                            style={{ verticalAlign: 'baseline', textDecoration: 'none' }}
                                            onClick={() => setMontoApertura(saldoSugerido.toString())}
                                        >
                                            S/ {saldoSugerido.toFixed(2)}
                                        </button>
                                    </small>
                                </div>
                            )}
                            <small className="form-text text-muted d-block mt-2">Ingrese el efectivo con el que abrirá la caja</small>
                        </div>
                        <button className="btn btn-primary btn-lg w-100" onClick={handleAbrirCaja} style={{ height: '55px' }}>
                            <i className="flaticon-381-plus me-2"></i> Abrir Caja con este Monto
                        </button>
                    </div>
                </div>
            );
        }

        const totalRecaudado = isGlobal ? data.total_recaudado : data.total_recaudado;

        return (
            <div className={`status-card ${isGlobal ? 'status-global' : 'status-open'} ${theme === 'dark' ? 'dark-mode' : ''}`}>
                <div className="status-header">
                    <div className="status-icon">
                        <i className={`fas fa-${isGlobal ? 'globe-americas' : 'cash-register'}`}></i>
                    </div>

                    <div className="status-info">
                        <h2>{isGlobal ? 'RESUMEN GLOBAL DEL DÍA' : 'MI CAJA ACTUAL'}</h2>
                        <div className="session-time d-flex align-items-center gap-2">
                            {isGlobal ? (
                                <>
                                    <span>Total recaudado el:</span>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        style={{ width: 'auto', display: 'inline-block' }}
                                        value={globalDate}
                                        onChange={(e) => setGlobalDate(e.target.value)}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className="d-flex flex-column">
                                        <span>Sesión iniciada: {new Date(data.fecha_apertura).toLocaleString('es-PE', { dateStyle: 'full', timeStyle: 'short' })}</span>

                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ALERTAS DE PENDIENTES */}
                {!isGlobal && pendientes.length > 0 && (
                    <div className="alert alert-warning d-flex align-items-center justify-content-between mb-4 shadow-sm" style={{ borderLeft: '5px solid #ffc107' }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="icon-circle bg-warning text-white p-3 rounded-circle">
                                <i className="flaticon-381-bell" style={{ fontSize: '1.5rem' }}></i>
                            </div>
                            <div>
                                <h5 className="mb-1 fw-bold">Tienes {pendientes.length} transferencia(s) pendiente(s) por recibir</h5>
                                <p className="mb-0 small text-muted">Acepta el dinero para que se refleje en tu saldo.</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            {pendientes.map(p => (
                                <button key={p.id} className="btn btn-dark btn-sm d-flex align-items-center gap-2" onClick={() => handleAceptarHabilitacion(p)}>
                                    <span>Recibir <strong>S/ {parseFloat(p.monto).toFixed(2)}</strong> de {p.emisor?.username}</span>
                                    <i className="fas fa-check-circle text-success"></i>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="dashboard-content">
                    {/* MAIN STATS ROW */}
                    <div className="main-stats-grid">
                        <div className="stat-card total-recaudado">
                            <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
                            <div className="stat-data">
                                <span className="stat-label">Total Recaudado</span>
                                <div className="stat-value-container">
                                    <span className="stat-value">S/ {totalRecaudado?.toFixed(2) || '0.00'}</span>
                                    {!isGlobal && (
                                        <button
                                            className="stat-view-btn income"
                                            onClick={() => {
                                                setModalFilter('INCOME');
                                                setShowDetailsModal(true);
                                            }}
                                            title="Ver Detalle"
                                        >
                                            <i className="fas fa-eye"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!isGlobal && (
                            <>
                                <div className="stat-card saldo-efectivo balance-hoverable">
                                    <div className="stat-icon"><i className="fas fa-wallet"></i></div>
                                    <div className="stat-data">
                                        <span className="stat-label">Saldo Efectivo en Caja</span>
                                        <div className="stat-value-container">
                                            <span className="stat-value">S/ {data.saldo_actual_efectivo?.toFixed(2)}</span>
                                        </div>
                                        <div className="mt-1">
                                            <small className="text-muted" style={{ fontWeight: 500 }}>
                                                <i className="fas fa-arrow-right me-1" style={{ fontSize: '0.7rem', opacity: 0.6 }}></i>
                                                Inicio: S/ {data.monto_inicial?.toFixed(2)}
                                            </small>
                                        </div>
                                    </div>

                                    {/* TOOLTIP SUMMARY */}
                                    <div className="balance-tooltip shadow-lg animate__animated animate__fadeIn">
                                        <div className="tooltip-header">
                                            <i className="fas fa-info-circle me-2"></i> RESUMEN DE LIQUIDACIÓN
                                        </div>
                                        <div className="tooltip-body p-0">
                                            <table className="table table-sm table-borderless mb-0">
                                                <tbody>
                                                    {/* Session calculation section */}
                                                    <tr className="bg-light-gray" style={{ backgroundColor: '#f8f9fa' }}>
                                                        <td colSpan="2" className="fw-bold x-small p-2 text-primary border-bottom">
                                                            <i className="fas fa-history me-1"></i> RESUMEN DE SESIÓN (ACTUAL)
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="ps-3 text-muted">Saldo de Apertura</td>
                                                        <td className="text-end fw-bold">S/ {data.monto_inicial?.toFixed(2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="ps-3 text-success">(+) Recaudación Hoy</td>
                                                        <td className="text-end text-success">S/ {(data.desglose_ingresos?.EFECTIVO || 0).toFixed(2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="ps-3 text-danger">(-) Gastos Hoy</td>
                                                        <td className="text-end text-danger">- S/ {(data.total_egresos_modulo || 0).toFixed(2)}</td>
                                                    </tr>
                                                    <tr className="border-bottom">
                                                        <td className="ps-3 text-muted">(-) Depósitos Hoy</td>
                                                        <td className="text-end">- S/ {(data.total_movimientos_caja || 0).toFixed(2)}</td>
                                                    </tr>

                                                    {/* Physical composition section */}
                                                    <tr className="bg-light-gray" style={{ backgroundColor: '#f8f9fa' }}>
                                                        <td colSpan="2" className="fw-bold x-small p-2 text-success border-bottom">
                                                            <i className="fas fa-wallet me-1"></i> COMPOSICIÓN DEL EFECTIVO
                                                        </td>
                                                    </tr>

                                                    {/* Concept Breakdown from Analysis Data - Flattened, Cleaned & Squared */}
                                                    {analysisData && analysisData.grupos && (() => {
                                                        const conceptMap = {}; // label -> balance
                                                        let sumConcepts = 0;

                                                        analysisData.grupos.forEach(group => {
                                                            if (group.key !== 'CAJA_GENERAL' || !group.conceptos_detalle) return;
                                                            Object.values(group.conceptos_detalle).forEach(sub => {
                                                                const rec = sub.desglose_ingresos?.EFECTIVO || 0;
                                                                const egr = sub.desglose_egresos?.EFECTIVO || 0;
                                                                const dep = sub.desglose_depositos?.EFECTIVO || 0;
                                                                const bal = rec - egr - dep;

                                                                if (bal > 0.01) {
                                                                    // Clean label: Remove VACACIONAL, ACADEMIA, years and underscores
                                                                    let cleanLabel = (sub.label || sub.concepto || 'OTROS')
                                                                        .toUpperCase()
                                                                        .replace(/VACACIONAL|ACADEMIA|2024|2025|2026/g, '')
                                                                        .replace(/_/g, ' ')
                                                                        .trim();

                                                                    if (!cleanLabel) cleanLabel = 'INGRESOS VARIOS';

                                                                    conceptMap[cleanLabel] = (conceptMap[cleanLabel] || 0) + bal;
                                                                    sumConcepts += bal;
                                                                }
                                                            });
                                                        });

                                                        const targetTotal = data.saldo_actual_efectivo || 0;
                                                        const discrepancy = targetTotal - sumConcepts;

                                                        const sortedConcepts = Object.entries(conceptMap)
                                                            .sort((a, b) => b[1] - a[1]);

                                                        return (
                                                            <div className="composition-scroll">
                                                                <table className="table table-sm table-borderless mb-0">
                                                                    <tbody>
                                                                        {sortedConcepts.map(([label, bal]) => (
                                                                            <tr key={label} className="concept-row">
                                                                                <td className="ps-3 small">
                                                                                    <span className="dot me-1" style={{ backgroundColor: '#10b981' }}></span>
                                                                                    {label}
                                                                                </td>
                                                                                <td className="text-end small">S/ {bal.toFixed(2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                        {Math.abs(discrepancy) > 0.01 && (
                                                                            <tr className="concept-row opacity-75">
                                                                                <td className="ps-3 small text-muted">
                                                                                    <i className="fas fa-minus-circle me-1 small"></i>
                                                                                    {discrepancy < 0 ? 'Salidas/Depósitos generales' : 'Saldos por asignar'}
                                                                                </td>
                                                                                <td className="text-end small text-muted">S/ {discrepancy.toFixed(2)}</td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        );
                                                    })()}

                                                    <tr className="bg-primary text-white mt-1">
                                                        <td className="fw-bold p-2 text-white">DEBE HABER EN CAJA</td>
                                                        <td className="text-end fw-bold p-2 text-white">S/ {data.saldo_actual_efectivo?.toFixed(2)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>

                                            {(!analysisData || loadingAnalysis) && (
                                                <div className="p-2 text-center small text-muted">
                                                    <i className="fas fa-spinner fa-spin me-1"></i> Cargando composición...
                                                </div>
                                            )}
                                        </div>
                                        <div className="tooltip-footer">
                                            <i className="fas fa-check-circle me-1"></i> Cuadrado con el saldo neto de la sesión
                                        </div>
                                    </div>
                                </div>
                                <div className="stat-card total-egresos">
                                    <div className="stat-icon"><i className="fas fa-receipt"></i></div>
                                    <div className="stat-data">
                                        <span className="stat-label">Total Salidas</span>
                                        <div className="stat-value-container">
                                            <span className="stat-value">S/ {data.total_egresos?.toFixed(2)}</span>
                                            <button
                                                className="stat-view-btn expense"
                                                onClick={() => {
                                                    setModalFilter('EXPENSE');
                                                    setShowDetailsModal(true);
                                                }}
                                                title="Ver Detalle"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        {/* RENDER MOVEMENTS MODAL */}
                        {sesionActual && (
                            <CajaMovimientosModal
                                show={showDetailsModal}
                                onClose={() => {
                                    setShowDetailsModal(false);
                                    setModalFilter('ALL');
                                }}
                                sesionId={sesionActual.sesion_id || sesionActual.id}
                                filter={modalFilter}
                            />
                        )}
                        {isGlobal && (
                            <div className="stat-card saldo-efectivo">
                                <div className="stat-icon"><i className="fas fa-globe-americas"></i></div>
                                <div className="stat-data">
                                    <span className="stat-label">Vista Administrativa</span>
                                    <span className="stat-value">Global</span>
                                    <small>Visualizando todas las sedes</small>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* DETAILED BREAKDOWN ROW */}
                    <h4 className="breakdown-title">Desglose por Medio de Pago</h4>
                    <div className="breakdown-grid">
                        <div className="mini-card method-efectivo">
                            <div className="method-icon"><i className="fas fa-money-bill-wave"></i></div>
                            <div className="method-details">
                                <span className="method-name">Efectivo</span>
                                <span className="method-amount">S/ {breakdown?.EFECTIVO?.toFixed(2) || '0.00'}</span>
                            </div>
                        </div>
                        <div className="mini-card method-yape">
                            <div className="method-icon"><i className="fas fa-qrcode"></i></div>
                            <div className="method-details">
                                <span className="method-name">Yape</span>
                                <span className="method-amount">S/ {breakdown?.YAPE?.toFixed(2) || '0.00'}</span>
                            </div>
                        </div>
                        <div className="mini-card method-plin">
                            <div className="method-icon"><i className="fas fa-qrcode"></i></div>
                            <div className="method-details">
                                <span className="method-name">Plin</span>
                                <span className="method-amount">S/ {breakdown?.PLIN?.toFixed(2) || '0.00'}</span>
                            </div>
                        </div>
                        <div className="mini-card method-banco">
                            <div className="method-icon"><i className="fas fa-university"></i></div>
                            <div className="method-details">
                                <span className="method-name">Bancos/Dep.</span>
                                <span className="method-amount">
                                    S/ {((breakdown?.TRANSFERENCIA || 0) + (breakdown?.DEPOSITO || 0) + (breakdown?.TARJETA || 0)).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>


                    <div className="dashboard-actions">
                        <button className="btn btn-warning btn-lg" onClick={() => { fetchEstadoCaja(); fetchGlobalSummary(); fetchHistorial(activeTab === 'global'); }}>
                            <i className="flaticon-381-refresh"></i> Actualizar
                        </button>
                        {!isGlobal && (
                            <button className="btn btn-danger btn-lg" onClick={() => {
                                console.log('🟡 GestionCaja: Realizar Cierre button clicked');
                                console.log('🟡 GestionCaja: Opening Arqueo modal');
                                setShowArqueo(true);
                            }}>
                                <i className="flaticon-381-calculator"></i> Realizar Cierre
                            </button>
                        )}
                        {!isGlobal && sesionActual && (
                            <button className="btn btn-info btn-lg text-white" onClick={() => setShowEntregarModal(true)} style={{ marginLeft: '10px' }}>
                                <i className="flaticon-381-send"></i> Entregar Efectivo
                            </button>
                        )}
                    </div>
                </div>
            </div >
        );
    };

    const getDisplayName = (fullName) => {
        if (!fullName) return '';
        if (!fullName.includes(' ')) return fullName;
        // Assume "Apellidos Nombres" -> Take last word as Name
        const parts = fullName.trim().split(/\s+/);
        const name = parts[parts.length - 1];
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    return (
        <div className={`caja-dashboard ${theme === 'dark' ? 'dark-mode' : ''}`} data-theme={theme}>
            {/* Conditional Rendering for Excel View */}
            {showArqueo ? (
                <ArqueoCaja
                    onConfirm={handleCerrarCajaConfirm}
                    onCancel={() => setShowArqueo(false)}
                />
            ) : null}

            {/* NEW: Cash Partitioning & Deposit Mode */}
            {activeTab === 'reporte_diario' ? (
                <div className="reporte-diario-wrapper">
                    <ArqueoDistribucion onClose={() => setActiveTab('personal')} />
                </div>
            ) : (
                <>
                    {/* MODAL DE ENTREGA */}
                    <ModalEntregarEfectivo
                        show={showEntregarModal}
                        onClose={() => {
                            setShowEntregarModal(false);
                            fetchEstadoCaja(); // Refresh saldo
                            fetchHistorial();
                        }}
                        sesionActual={sesionActual}
                    />


                    {/* TAB NAVIGATION */}
                    <div className="caja-tabs">
                        <button
                            className={`caja-tab ${activeTab === 'personal' ? 'active' : ''}`}
                            onClick={() => setActiveTab('personal')}
                        >
                            <i className="fas fa-user-circle"></i> Mi Caja
                        </button>
                        <button
                            className={`caja-tab ${activeTab === 'global' ? 'active' : ''}`}
                            onClick={() => setActiveTab('global')}
                        >
                            <i className="fas fa-globe-americas"></i> Resumen Global (Admin)
                        </button>
                        <button
                            className={`caja-tab ${activeTab === 'reporte_diario' ? 'active' : ''}`}
                            onClick={() => setActiveTab('reporte_diario')}
                        >
                            <i className="fas fa-sitemap"></i> Arqueo y Distribución
                        </button>

                    </div>

                    {/* RENDER ACTIVE DASHBOARD */}
                    {activeTab !== 'reporte_diario' && renderDashboard(activeTab === 'global')}

                    {/* HISTORY TABLE */}
                    {activeTab !== 'reporte_diario' && (
                        <div className={`history-section ${theme === 'dark' ? 'dark-mode' : ''}`}>
                            <div className="section-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                                <h3>Historial de Sesiones</h3>
                                <div className="d-flex align-items-center flex-wrap gap-2" style={{ marginRight: 'auto', marginLeft: '20px' }}>
                                    <span className="text-muted small me-1">Buscar apertura:</span>
                                    <input 
                                        type="date" 
                                        className="form-control form-control-sm"
                                        style={{ 
                                            maxWidth: '160px', 
                                            borderRadius: '6px', 
                                            border: '1px solid #dee2e6',
                                            backgroundColor: theme === 'dark' ? '#2c2c2c' : '#ffffff',
                                            color: theme === 'dark' ? '#ffffff' : '#333333'
                                        }}
                                        value={searchDate}
                                        onChange={(e) => setSearchDate(e.target.value)}
                                    />
                                    {searchDate && (
                                        <button 
                                            className="btn btn-link btn-sm text-danger p-0 ms-1 text-decoration-none"
                                            onClick={() => setSearchDate('')}
                                        >
                                            <i className="fas fa-times-circle"></i> Limpiar
                                        </button>
                                    )}
                                </div>
                                <button className="btn btn-outline-primary btn-sm">
                                    <i className="flaticon-381-download"></i> Exportar
                                </button>
                            </div>

                            <div className="table-responsive">
                                <table className={`table table-hover history-table ${theme === 'dark' ? 'table-dark' : ''}`}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Usuario</th>
                                            <th>Apertura</th>
                                            <th>Cierre</th>
                                            <th>Estado</th>
                                            <th className="highlight-col">Inicial</th>
                                            <th>Recaudación</th>
                                            <th>Ventas</th>
                                            <th className="highlight-col">Total Ing.</th>
                                            <th>Gastos</th>
                                            <th>Depósitos</th>
                                            <th className="highlight-col">Total Egr.</th>
                                            <th>Esperado</th>
                                            <th className="highlight-col">Real</th>
                                            <th>Faltante</th>
                                            <th>Sobrante</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historial.length === 0 ? (
                                            <tr>
                                                <td colSpan="17" className="text-center py-4">
                                                    <i className="flaticon-381-folder" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                                                    <p className="mt-2 text-muted">No hay sesiones registradas</p>
                                                </td>
                                            </tr>
                                        ) : (
                                            historial.map(sesion => (
                                                <tr key={sesion.id}>
                                                    <td><strong>{sesion.id}</strong></td>
                                                    <td>
                                                        <div className="user-cell">
                                                            <span>{getDisplayName(sesion.nombre_usuario)}</span>
                                                        </div>
                                                    </td>
                                                    <td>{new Date(sesion.fecha_apertura).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                                                    <td>{sesion.fecha_cierre ? new Date(sesion.fecha_cierre).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                                                    <td>
                                                        <span className={`badge badge-${sesion.estado === 'CERRADA' ? 'danger' : 'success'}`}>
                                                            {sesion.estado}
                                                        </span>
                                                    </td>
                                                    <td className="amount highlight-saldo-inicial"><strong>S/ {parseFloat(sesion.monto_inicial).toFixed(2)}</strong></td>
                                                    <td className="amount amount-positive">S/ {parseFloat(sesion.ingresos_sistema || 0).toFixed(2)}</td>
                                                    <td className="amount placeholder-col">S/ 0.00</td>
                                                    <td className="amount amount-positive highlight-total-ingresos"><strong>S/ {(parseFloat(sesion.monto_inicial) + parseFloat(sesion.ingresos_sistema || 0)).toFixed(2)}</strong></td>
                                                    <td className="amount">S/ {parseFloat(sesion.gastos || 0).toFixed(2)}</td>
                                                    <td className="amount">S/ {parseFloat(sesion.depositos || 0).toFixed(2)}</td>
                                                    <td className="amount amount-negative highlight-total-egresos">
                                                        <strong>S/ {(parseFloat(sesion.gastos || 0) + parseFloat(sesion.depositos || 0)).toFixed(2)}</strong>
                                                    </td>
                                                    <td className="amount">
                                                        S/ {(parseFloat(sesion.monto_inicial) + parseFloat(sesion.ingresos_sistema || 0) - (parseFloat(sesion.gastos || 0) + parseFloat(sesion.depositos || 0))).toFixed(2)}
                                                    </td>
                                                    <td className="amount highlight-saldo-real"><strong>{sesion.monto_final_real ? `S/ ${parseFloat(sesion.monto_final_real).toFixed(2)}` : '-'}</strong></td>
                                                    <td className="amount amount-negative">
                                                        {(() => {
                                                            const esperado = parseFloat(sesion.monto_inicial) + parseFloat(sesion.ingresos_sistema || 0) - (parseFloat(sesion.gastos || 0) + parseFloat(sesion.depositos || 0));
                                                            const real = parseFloat(sesion.monto_final_real || 0);
                                                            const diff = real - esperado;
                                                            return diff < -0.01 ? `S/ ${Math.abs(diff).toFixed(2)}` : '-';
                                                        })()}
                                                    </td>
                                                    <td className="amount amount-positive">
                                                        {(() => {
                                                            const esperado = parseFloat(sesion.monto_inicial) + parseFloat(sesion.ingresos_sistema || 0) - (parseFloat(sesion.gastos || 0) + parseFloat(sesion.depositos || 0));
                                                            const real = parseFloat(sesion.monto_final_real || 0);
                                                            const diff = real - esperado;
                                                            return diff > 0.01 ? `S/ ${diff.toFixed(2)}` : '-';
                                                        })()}
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons-row">
                                                            <button
                                                                className="action-btn-mini view"
                                                                title="Ver Movimientos"
                                                                onClick={() => {
                                                                    setSelectedSesionId(sesion.id);
                                                                    setModalFilter('ALL');
                                                                    setShowDetailsModal(true);
                                                                }}
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </button>
                                                            <button
                                                                className="action-btn-mini edit"
                                                                title="Editar Sesión"
                                                                onClick={() => handleEditClick(sesion)}
                                                            >
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </button>
                                                            <button
                                                                className="action-btn-mini report"
                                                                title="Imprimir Reporte"
                                                                onClick={() => handlePrintReport(sesion.id)}
                                                                style={{ backgroundColor: '#6c757d', color: 'white' }}
                                                            >
                                                                <i className="fas fa-print"></i>
                                                            </button>
                                                            {user?.rol === 'ADMIN' && sesion.estado === 'CERRADA' && (
                                                                <button
                                                                    className="action-btn-mini reopen"
                                                                    title="Reabrir Caja"
                                                                    onClick={() => handleReabrirSesion(sesion.id)}
                                                                >
                                                                    <i className="flaticon-381-back"></i>
                                                                </button>
                                                            )}
                                                            {user?.rol === 'ADMIN' && (
                                                                <button
                                                                    className="action-btn-mini delete"
                                                                    title="Eliminar Sesión"
                                                                    onClick={() => handleDeleteSesion(sesion.id)}
                                                                >
                                                                    <i className="flaticon-381-trash"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
                                    <div className="text-muted small">
                                        Mostrando página {currentPage} de {totalPages} ({totalItems} sesiones en total)
                                    </div>
                                    <nav aria-label="Navegación de historial">
                                        <ul className="pagination pagination-circle mb-0" style={{ display: 'flex', gap: '5px', listStyle: 'none', padding: 0 }}>
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    style={{ 
                                                        border: '1px solid #dee2e6', 
                                                        borderRadius: '50%', 
                                                        width: '35px', 
                                                        height: '35px', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                        backgroundColor: theme === 'dark' ? '#2c2c2c' : '#ffffff',
                                                        color: theme === 'dark' ? '#ffffff' : '#333333'
                                                    }}
                                                    onClick={() => fetchHistorial(activeTab === 'global', currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                >
                                                    <i className="fa fa-chevron-left"></i>
                                                </button>
                                            </li>
                                            
                                            {(() => {
                                                const pages = [];
                                                const maxVisible = 5;
                                                let start = Math.max(1, currentPage - 2);
                                                let end = Math.min(totalPages, start + maxVisible - 1);
                                                if (end - start < maxVisible - 1) {
                                                    start = Math.max(1, end - maxVisible + 1);
                                                }

                                                for (let i = start; i <= end; i++) {
                                                    pages.push(
                                                        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                style={{ 
                                                                    border: '1px solid #dee2e6', 
                                                                    borderRadius: '50%', 
                                                                    width: '35px', 
                                                                    height: '35px', 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'center', 
                                                                    cursor: 'pointer',
                                                                    backgroundColor: currentPage === i ? '#3b82f6' : (theme === 'dark' ? '#2c2c2c' : '#ffffff'),
                                                                    color: currentPage === i ? 'white' : (theme === 'dark' ? '#ffffff' : '#333333')
                                                                }}
                                                                onClick={() => fetchHistorial(activeTab === 'global', i)}
                                                            >
                                                                {i}
                                                            </button>
                                                        </li>
                                                    );
                                                }
                                                return pages;
                                            })()}

                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    style={{ 
                                                        border: '1px solid #dee2e6', 
                                                        borderRadius: '50%', 
                                                        width: '35px', 
                                                        height: '35px', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                        backgroundColor: theme === 'dark' ? '#2c2c2c' : '#ffffff',
                                                        color: theme === 'dark' ? '#ffffff' : '#333333'
                                                    }}
                                                    onClick={() => fetchHistorial(activeTab === 'global', currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                >
                                                    <i className="fa fa-chevron-right"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}

                            {/* Modal de Edición Administrativa */}
                            {showEditModal && (
                                <div className="modal-overlay">
                                    <div className="modal-container" style={{ maxWidth: '500px' }}>
                                        <div className="modal-header">
                                            <h3>Editar Sesión #{sesionToEdit?.id}</h3>
                                            <button className="btn-close" onClick={() => setShowEditModal(false)}>×</button>
                                        </div>
                                        <div className="modal-body p-4">
                                            <div className="mb-3">
                                                <label className="form-label">Monto Inicial (S/)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editForm.monto_inicial}
                                                    onChange={(e) => setEditForm({ ...editForm, monto_inicial: e.target.value })}
                                                />
                                                <small className="text-muted">Si cambias esto, el saldo esperado se recalculará automáticamente.</small>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Monto Real de Cierre (S/)</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editForm.monto_final_real}
                                                    onChange={(e) => setEditForm({ ...editForm, monto_final_real: e.target.value })}
                                                />
                                                <small className="text-muted">Es lo que realmente hubo físicamente en caja al cerrar.</small>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Ingresos del Sistema (S/)</label>
                                                <div className="input-group">
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className={`form-control ${Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) > 0.01 ? 'border-warning' : 'border-success'}`}
                                                        value={editForm.ingresos_sistema}
                                                        onChange={(e) => setEditForm({ ...editForm, ingresos_sistema: e.target.value })}
                                                        readOnly={Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) <= 0.01}
                                                    />
                                                    {Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) > 0.01 && (
                                                        <button
                                                            className="btn btn-outline-success"
                                                            type="button"
                                                            onClick={() => setEditForm({ ...editForm, ingresos_sistema: ingresosCalculados.toFixed(2) })}
                                                            title="Usar monto calculado"
                                                        >
                                                            <i className="fas fa-sync-alt"></i>
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="mt-2">
                                                    <small className={`${Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) > 0.01 ? 'text-warning' : 'text-success'}`}>
                                                        <i className={`fas fa-${Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) > 0.01 ? 'exclamation-triangle' : 'check-circle'} me-1`}></i>
                                                        {Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) > 0.01
                                                            ? `Calculado de movimientos: S/ ${ingresosCalculados.toFixed(2)} (Diferencia: S/ ${Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados).toFixed(2)})`
                                                            : `Coincide con movimientos: S/ ${ingresosCalculados.toFixed(2)}`
                                                        }
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Observaciones</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={editForm.observaciones}
                                                    onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })}
                                                    placeholder="Motivo de la corrección..."
                                                />
                                            </div>

                                            {Math.abs(parseFloat(editForm.ingresos_sistema) - ingresosCalculados) <= 0.01 ? (
                                                <div className="alert alert-success mt-3" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-check-circle me-2"></i>
                                                    Los ingresos coinciden con los movimientos registrados. Campo bloqueado para mantener integridad.
                                                </div>
                                            ) : (
                                                <div className="alert alert-warning mt-3" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                                    <strong>Descuadre detectado:</strong> Los ingresos guardados no coinciden con los movimientos.
                                                    Puedes corregir el monto manualmente o usar el botón de sincronizar.
                                                </div>
                                            )}
                                        </div>
                                        <div className="modal-footer">
                                            <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                                            <button className="btn btn-primary" onClick={handleSaveEdit}>Guardar Cambios</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* MODAL FOR SESSION DETAILS FROM HISTORY */}
                            {selectedSesionId && (
                                <CajaMovimientosModal
                                    show={showDetailsModal}
                                    onClose={() => {
                                        setShowDetailsModal(false);
                                        setSelectedSesionId(null);
                                        setModalFilter('ALL');
                                    }}
                                    sesionId={selectedSesionId}
                                    filter={modalFilter}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default GestionCaja;
